import React, { useState } from "react";

import { AdministrationCard } from "@common/components/AdministrationCard/AdministrationCard";
import Button from "@common/components/Button/Button";
import Heading from "@common/components/Heading/Heading";
import Input from "@common/components/Input/Input";
import PageTitle from "@common/components/PageTitle/PageTitle";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { useForm } from "@common/hooks/useForm";
import { Workspace } from "@common/models";
import { isAdmin } from "@common/models/Member.model";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import identifiers from "@common/util/identifiers.json";
import { t } from "@common/util/translator";
import { Validators } from "@common/util/Validators";
import LogoComponent from "@main/pages/shared/components/LogoComponent/LogoComponent";
import { getRandomColor } from "@main/pages/shared/helpers/getRandomColorHelper";

import * as Styled from "./GeneralView.styles";
import DangerZoneCard from "../../components/DangerZone/DangerZoneCard";
import useGeneralViewApiConsumer from "../../consumers/useGeneralViewApiConsumer";

interface FormState {
  workspaceName: string;
}

const validators = {
  workspaceName: (name: string) => {
    if (!Validators.minLength(name, 3)) return new Error("Type 3 characters or more.");
  },
};

export default function GeneralView(): JSX.Element {
  const {
    updateWorkspaceInfo,
    selectedWorkspace,
    deleteWorkspace,
    loading: deleteLoading,
    uploading,
    updateWorkspaceLogo,
    deleteWorkspaceLogo,
  } = useGeneralViewApiConsumer();

  const { formState, formError, handleFormChange, handleFormSubmit } = useForm<FormState>({
    initialState: {
      workspaceName: selectedWorkspace?.name || "",
    },
    validators,
    onSubmit: handleSubmit,
  });
  const [loading, setLoading] = useState(false);

  const { workspaceName } = formState;
  const { workspaceName: workspaceErr } = formError;

  async function handleSubmit() {
    setLoading(true);
    const newName = await updateWorkspaceInfo(workspaceName);
    handleFormChange("workspaceName", newName);
    setLoading(false);
  }

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleFormChange(e.currentTarget.name, e.currentTarget.value);
  };

  return (
    <>
      <Title />
      <PageWrap>
        <BasicsCard
          name={workspaceName}
          nameErr={workspaceErr}
          handleInput={handleInput}
          handleSubmit={handleFormSubmit}
          selectedWorkspace={selectedWorkspace}
          loading={loading}
          isAdmin={isAdmin(selectedWorkspace?.role)}
          uploading={uploading}
          updateLogo={updateWorkspaceLogo}
          deleteLogo={deleteWorkspaceLogo}
        />
        {isAdmin(selectedWorkspace?.role) && (
          <DangerZoneCard
            deleteWorkspace={deleteWorkspace}
            loading={deleteLoading}
          />
        )}
      </PageWrap>
    </>
  );
}

const Title = () => (
  <PageTitle>
    <Heading
      as="h1"
      heading1
      marginBottom={8}
      testId={identifiers["workspaceAdministration.header.general"]}
    >
      {t("administration.general.title")}
    </Heading>
    <p>{t("administration.general.subtitle")}</p>
  </PageTitle>
);

interface BasicsCardProps {
  name: string;
  handleInput: (e: React.FormEvent<HTMLInputElement>) => void;
  handleSubmit: () => void;
  selectedWorkspace?: Workspace;
  nameErr: Error | undefined;
  loading: boolean;
  isAdmin: boolean;
  uploading?: boolean;
  updateLogo: (file: File) => void;
  deleteLogo: (toggle: () => void) => void;
}

const BasicsCard = ({
  name,
  handleInput,
  handleSubmit,
  selectedWorkspace,
  nameErr,
  loading,
  isAdmin,
  uploading,
  updateLogo,
  deleteLogo,
}: BasicsCardProps) => {
  const isButtonDisabled = name === selectedWorkspace?.name || !isAdmin;
  const tooltipText = !isAdmin ? "Only admins can change workspace name" : "Please change workspace name first";

  return (
    <AdministrationCard title={t("administration.general.card.heading")}>
      <Styled.InputContainer>
        <Flex
          $column
          $gap={2}
        >
          <LogoComponent
            title="Workspace logo"
            subtitle="Optional • Visible to workspace members only."
            image={selectedWorkspace?.logo_url ?? undefined}
            backgroundColor={getRandomColor(selectedWorkspace?.id).background}
            textColor={getRandomColor(selectedWorkspace?.id).text}
            letters={name[0]?.toLocaleUpperCase()}
            onUpdatePicture={updateLogo}
            onDeletePicture={deleteLogo}
            loading={uploading}
            canChange={isAdmin}
          />
          <Input
            type="text"
            name="workspaceName"
            label="Workspace name"
            value={name}
            error={!!nameErr}
            helper={nameErr && nameErr.message}
            onChange={handleInput}
            disabled={!isAdmin || loading}
            testId={identifiers["general.input.workspaceName"]}
          />
        </Flex>
      </Styled.InputContainer>

      <Button
        type="submit"
        size="small"
        onClick={handleSubmit}
        disabled={isButtonDisabled || loading}
        testId={identifiers["general.button.save"]}
        tooltip={{
          position: "top",
          text: tooltipText,
          disabled: !isButtonDisabled,
        }}
        loadingText={loading && "Saving..."}
      >
        {"Save"}
      </Button>
    </AdministrationCard>
  );
};
